/* Progressive enhancement only. Shopify owns products, accounts, cart and checkout. */
(() => {
  'use strict';
  document.documentElement.classList.replace('no-js', 'js');
  const dialogOrigins = new WeakMap();
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function closeDisclosures(except = null) {
    document.querySelectorAll('details[data-disclosure][open]').forEach((details) => {
      if (details !== except && !details.contains(except)) details.open = false;
    });
  }

  function openContact({ origin, topic = '', vehicle = '', product = '' } = {}) {
    const dialog = document.querySelector('dialog[data-contact-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    const focusOrigin = origin?.closest('details[data-disclosure]')?.querySelector('summary') || origin;
    closeDisclosures();
    if (focusOrigin) dialogOrigins.set(dialog, focusOrigin);
    dialog.dispatchEvent(new CustomEvent('leonparc:configure-contact', {
      bubbles: true, detail: { topic, vehicle, product }
    }));
    if (!dialog.open) dialog.showModal();
    dialog.querySelector('[data-contact-topic]:checked, [data-contact-topic]')?.focus();
    return true;
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const contactLink = event.target.closest('a[data-contact-trigger]');
    if (contactLink) {
      if (openContact({ origin: contactLink, topic: contactLink.dataset.contactTopic || '', product: contactLink.dataset.contactProduct || '' })) {
        event.preventDefault();
      }
      return;
    }
    const openLink = event.target.closest('[data-open-dialog]');
    if (openLink) {
      const dialog = document.getElementById(openLink.dataset.openDialog);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        closeDisclosures();
        dialogOrigins.set(dialog, openLink);
        dialog.showModal();
        dialog.querySelector('input[type="search"]')?.focus();
      }
      return;
    }
    const closeButton = event.target.closest('[data-close-dialog]');
    if (closeButton) { closeButton.closest('dialog')?.close(); return; }
    if (event.target.matches('dialog[open]')) {
      const box = event.target.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) event.target.close();
    }
    const anchor = event.target.closest('a[href]');
    if (anchor && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0 && !anchor.hasAttribute('download') && anchor.target !== '_blank') {
      let targetURL;
      try { targetURL = new URL(anchor.href, location.href); } catch { return; }
      if (targetURL.hash && targetURL.origin === location.origin && targetURL.pathname.replace(/\/$/, '') === location.pathname.replace(/\/$/, '')) {
        let id;
        try { id = decodeURIComponent(targetURL.hash.slice(1)); } catch { return; }
        const target = document.getElementById(id);
        if (target) {
          event.preventDefault();
          anchor.closest('dialog')?.close();
          closeDisclosures();
          // Keep Shopify preview / locale query parameters in place.
          history.replaceState(null, '', `${location.pathname}${location.search}${targetURL.hash}`);
          target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
          if (target.hasAttribute('tabindex')) target.focus({ preventScroll: true });
        }
      }
    }
    document.querySelectorAll('details[data-disclosure][open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
      else if (anchor && details.contains(anchor)) details.open = false;
    });
  });

  document.addEventListener('close', (event) => {
    if (event.target instanceof Element && event.target.matches('dialog')) {
      const origin = dialogOrigins.get(event.target);
      if (origin?.isConnected) origin.focus({ preventScroll: true });
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || document.querySelector('dialog[open]')) return;
    const open = document.querySelector('details[data-disclosure][open]');
    if (open) { event.preventDefault(); closeDisclosures(); open.querySelector('summary')?.focus(); }
  });
  document.addEventListener('shopify:section:unload', () => closeDisclosures());

  class VehicleFinder extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.form = this.querySelector('[data-vehicle-form]');
      this.make = this.querySelector('[data-make]');
      this.model = this.querySelector('[data-model]');
      this.year = this.querySelector('[data-year]');
      this.status = this.querySelector('[data-vehicle-status]');
      const fields = this.querySelector('[data-vehicle-fields]');
      if (!this.form || !this.make || !this.model || !this.year || !this.status || !fields) return;
      this.demo = this.dataset.mode === 'demo';
      if (this.demo) {
        try {
          const data = JSON.parse(this.dataset.demoOptions || '{}');
          if (!data || typeof data !== 'object' || Array.isArray(data) || Object.keys(data).length === 0) throw new Error('Geen merken');
          for (const [make, models] of Object.entries(data)) {
            if (!make.trim() || !models || typeof models !== 'object' || Array.isArray(models) || !Object.keys(models).length) throw new Error('Ongeldig merk');
            for (const [model, years] of Object.entries(models)) {
              if (!model.trim() || !Array.isArray(years) || !years.length || years.some((year) => !Number.isInteger(year) || year < 1886 || year > new Date().getFullYear() + 1)) throw new Error('Ongeldig bouwjaar');
            }
          }
          this.options = data;
          this.fillOptions(this.make, Object.keys(data), 'Kies merk');
        } catch {
          fields.disabled = true;
          this.status.textContent = 'De voorbeeldgegevens zijn niet bruikbaar. Neem contact op voor advies; er zijn geen productmatches bepaald.';
          return;
        }
      }
      fields.disabled = false;
      this.resetAfterMake();
      const opts = { signal: this.abort.signal };
      this.make.addEventListener('change', () => { this.resetAfterMake(); this.clearPreparedAdvice(); }, opts);
      this.model.addEventListener(this.demo ? 'change' : 'input', () => { this.resetAfterModel(); this.clearPreparedAdvice(); }, opts);
      this.year.addEventListener('input', () => this.clearPreparedAdvice(), opts);
      this.form.addEventListener('submit', (event) => this.prepareAdvice(event), opts);
    }
    disconnectedCallback() { this.abort?.abort(); }
    fillOptions(select, values, placeholder) {
      select.replaceChildren(new Option(placeholder, ''));
      for (const value of values) select.add(new Option(String(value), String(value)));
    }
    resetAfterMake() {
      this.model.value = '';
      this.year.value = '';
      this.model.disabled = !this.make.value;
      this.year.disabled = true;
      if (this.demo) {
        const models = Object.hasOwn(this.options, this.make.value) ? Object.keys(this.options[this.make.value]) : [];
        this.fillOptions(this.model, models, this.make.value ? 'Kies model' : 'Kies eerst merk');
        this.fillOptions(this.year, [], 'Kies eerst model');
      }
    }
    resetAfterModel() {
      this.year.value = '';
      this.year.disabled = !this.model.value.trim();
      if (this.demo) {
        const models = Object.hasOwn(this.options, this.make.value) ? this.options[this.make.value] : {};
        const years = Object.hasOwn(models, this.model.value) ? models[this.model.value] : [];
        this.fillOptions(this.year, years, this.model.value ? 'Kies bouwjaar' : 'Kies eerst model');
      }
    }
    clearPreparedAdvice() {
      this.status.textContent = '';
    }
    prepareAdvice(event) {
      event.preventDefault();
      if (!this.make.value || !this.model.value.trim() || !this.year.value || !this.form.reportValidity()) {
        this.status.textContent = 'Vul merk, model en bouwjaar in.';
        return;
      }
      const vehicle = `${this.make.value} ${this.model.value.trim()} (${this.year.value})`;
      if (this.demo) {
        this.status.textContent = `Voorbeeld: ${vehicle}. Dit toont alleen de bediening; er zijn geen productmatches gecontroleerd.`;
        return;
      }
      if (openContact({ origin: this.form.querySelector('button[type="submit"]'), vehicle })) {
        this.status.textContent = 'Vul je vraag en contactgegevens in het geopende contactformulier in.';
      } else {
        this.status.textContent = 'Je voertuiggegevens zijn ingevuld. Neem contact op via de contactgegevens onderaan de pagina.';
      }
    }
  }
  if (!customElements.get('lp-vehicle-finder')) customElements.define('lp-vehicle-finder', VehicleFinder);

  // Native Shopify variant controls: radios for a few options, select for many.
  // Only updates view state; the product form remains the source of truth.
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement || target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-product-variant]') || (target instanceof HTMLInputElement && !target.checked)) return;
    const form = target.closest('form, .lp-product__form--demo');
    const product = target.closest('[data-product]');
    const selected = target instanceof HTMLSelectElement ? target.selectedOptions[0] : target;
    if (!form || !product || !selected) return;
    const price = product.querySelector('[data-product-price]');
    const button = form.querySelector('[data-add-product]');
    if (price && selected.dataset.price) price.textContent = selected.dataset.price;
    if (button && !form.classList.contains('lp-product__form--demo')) {
      const unavailable = selected.dataset.available !== 'true';
      button.disabled = unavailable;
      const text = unavailable ? 'Niet beschikbaar' : 'Toevoegen aan winkelwagen';
      const label = button.querySelector('[data-add-product-label]');
      if (label) label.textContent = text;
      else button.textContent = text;
    }
    const availability = product.querySelector('[data-product-availability]');
    if (availability) availability.textContent = selected.dataset.available === 'true'
      ? 'Beschikbaar voor bestelling' : 'Deze uitvoering is momenteel niet beschikbaar';
    if (selected.dataset.mediaId) {
      product.querySelector('[data-gallery]')?.dispatchEvent(
        new CustomEvent('leonparc:variant-media', { detail: { id: selected.dataset.mediaId } })
      );
    }
    const url = new URL(location.href);
    url.searchParams.set('variant', selected.value);
    history.replaceState(null, '', url);
  });
})();


/* Progressive mobile footer disclosures. Without JS the native details remain
   open, so SEO links and keyboard navigation are never lost. */
(() => {
  'use strict';
  const mobile = window.matchMedia('(max-width: 767px)');
  function syncFooterMode() {
    const mode = mobile.matches ? 'mobile' : 'desktop';
    document.querySelectorAll('details[data-lp-footer-disclosure]').forEach((item) => {
      if (item.dataset.lpFooterMode === mode) return;
      item.open = !mobile.matches;
      item.dataset.lpFooterMode = mode;
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncFooterMode, { once: true });
  else syncFooterMode();
  if (typeof mobile.addEventListener === 'function') mobile.addEventListener('change', syncFooterMode);
  else if (typeof mobile.addListener === 'function') mobile.addListener(syncFooterMode);
  document.addEventListener('shopify:section:load', syncFooterMode);
  document.addEventListener('toggle', (event) => {
    const item = event.target;
    if (!mobile.matches || !(item instanceof HTMLDetailsElement) || !item.matches('[data-lp-footer-disclosure]') || !item.open) return;
    item.closest('.lp-footer')?.querySelectorAll('details[data-lp-footer-disclosure][open]').forEach((other) => {
      if (other !== item) other.open = false;
    });
  }, true);
})();
